import { useEffect, useMemo, useState } from "react";
import { Link, Navigate, useLocation, useNavigate, useSearchParams } from "react-router";
import { isAxiosError } from "axios";
import { errorToast } from "@/utils/constants";
import { buildClassCatalogPath } from "@/utils/catalogSlug";
import {
  isCheckoutPaymentComplete,
  isVerifyPaymentComplete,
  toCheckoutPayload,
} from "../api/landingCheckout.mappers";
import {
  checkoutClassService,
  verifyCheckoutPaymentService,
} from "../api/landingCheckout.service";
import { launchCheckoutPaymentGateway } from "../utils/landingPaymentGateway";
import CheckoutForm from "../components/CheckoutForm";
import LandingIcon from "../components/LandingIcon";
import { LandingErrorState, LandingLoadingState } from "../components/LandingState";
import OrderSummaryCard from "../components/OrderSummaryCard";
import { LANDING_CHECKOUT_TYPOGRAPHY, LANDING_PAGE_GUTTER } from "../constants/landing.constants";
import { LANDING_USE_MOCK } from "../config/landing.config";
import { getCourseBySlug } from "../data/landing.mock";
import { useClassView } from "../hooks/useClassView";
import type { CheckoutFormValues } from "../types/landing.types";
import { loadClassDetailSnapshot } from "../utils/classDetailSnapshot";
import {
  clearCheckoutSession,
  loadCheckoutSession,
  saveCheckoutSession,
  type CheckoutSessionState,
} from "../utils/checkoutSession";

export default function CheckoutPage() {
  const location = useLocation();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const [isSubmitting, setIsSubmitting] = useState(false);

  const sessionFromStateOrStorage = useMemo((): CheckoutSessionState | null => {
    const fromState = location.state as CheckoutSessionState | null;
    if (fromState?.course) {
      return fromState;
    }

    const fromSession = loadCheckoutSession();
    if (fromSession?.course) {
      return fromSession;
    }

    if (LANDING_USE_MOCK) {
      const categorySlug = searchParams.get("category") ?? "";
      const courseSlug =
        searchParams.get("class") ?? searchParams.get("course") ?? "";
      if (!categorySlug || !courseSlug) return null;
      const course = getCourseBySlug(categorySlug, courseSlug);
      if (!course) return null;
      return {
        course,
        levelId: categorySlug,
        classId: courseSlug,
        categorySlug,
        courseSlug,
      };
    }

    return null;
  }, [location.state, searchParams]);

  const apiLevelId =
    searchParams.get("levelId") ?? sessionFromStateOrStorage?.levelId ?? "";
  const apiClassId =
    searchParams.get("classId") ?? sessionFromStateOrStorage?.classId ?? "";
  const apiLevel1Id =
    searchParams.get("level1Id") ??
    sessionFromStateOrStorage?.level1Id ??
    "";

  const needsApiFetch =
    !LANDING_USE_MOCK &&
    Boolean(apiLevelId && apiClassId) &&
    (!sessionFromStateOrStorage?.course ||
      !sessionFromStateOrStorage.course.schoolId?.trim());

  const apiSnapshot = useMemo(() => {
    if (!needsApiFetch) return null;
    return loadClassDetailSnapshot(apiLevelId, apiClassId);
  }, [needsApiFetch, apiLevelId, apiClassId]);

  const { course: fetchedCourse, isLoading, error } = useClassView({
    classId: needsApiFetch ? apiClassId : "",
    levelId: needsApiFetch ? apiLevelId : "",
    level1Id: needsApiFetch ? apiLevel1Id || undefined : undefined,
    snapshot: apiSnapshot,
  });

  const checkoutData = useMemo((): CheckoutSessionState | null => {
    if (needsApiFetch && fetchedCourse) {
      return {
        course: fetchedCourse,
        levelId: apiLevelId,
        classId: apiClassId,
        level1Id: apiLevel1Id || undefined,
        categorySlug: sessionFromStateOrStorage?.categorySlug,
        courseSlug: sessionFromStateOrStorage?.courseSlug,
      };
    }

    if (sessionFromStateOrStorage?.course) {
      return sessionFromStateOrStorage;
    }

    return null;
  }, [
    sessionFromStateOrStorage,
    needsApiFetch,
    fetchedCourse,
    apiLevelId,
    apiClassId,
  ]);

  useEffect(() => {
    if (checkoutData?.course) {
      saveCheckoutSession(checkoutData);
    }
  }, [checkoutData]);

  if (needsApiFetch && isLoading) {
    return <LandingLoadingState message="Loading checkout…" />;
  }

  if (needsApiFetch && !isLoading && (error || !fetchedCourse?.schoolId)) {
    return (
      <>
        <LandingErrorState
          message={
            error ??
            "This class is missing school information required for checkout."
          }
        />
        <div className={`text-center ${LANDING_PAGE_GUTTER}`}>
          <Link
            to={
              checkoutData?.categorySlug && checkoutData?.courseSlug
                ? buildClassCatalogPath(
                    checkoutData.categorySlug,
                    checkoutData.courseSlug
                  )
                : apiLevelId && apiClassId
                  ? `/classes/level/${apiLevelId}/${apiClassId}`
                  : "/classes"
            }
            className="text-[14px] font-semibold text-landing-button hover:underline"
          >
            Back to class details
          </Link>
        </div>
      </>
    );
  }

  if (!checkoutData?.course) {
    return <Navigate to="/classes" replace />;
  }

  const handleSubmit = async (values: CheckoutFormValues) => {
    if (!LANDING_USE_MOCK && !checkoutData.course.schoolId?.trim()) {
      errorToast(
        "Checkout unavailable",
        "This class is missing school information. Please go back and try again."
      );
      return;
    }

    setIsSubmitting(true);
    try {
      if (LANDING_USE_MOCK) {
        await new Promise((resolve) => setTimeout(resolve, 1200));
      } else {
        const payload = toCheckoutPayload(values, checkoutData);
        const response = await checkoutClassService(payload);
        const result = response.data;

        if (!result) {
          throw new Error(response.message || "Checkout returned no data.");
        }

        const completeEnrollment = () => {
          clearCheckoutSession();
          navigate(
            `/checkout/success?email=${encodeURIComponent(values.email)}`,
            { replace: true }
          );
        };

        if (isCheckoutPaymentComplete(result)) {
          completeEnrollment();
          return;
        }

        if (!result.payment_gateway) {
          throw new Error("Payment gateway details are missing.");
        }

        // Allow closing Razorpay modal without leaving the button stuck loading
        setIsSubmitting(false);

        const razorpayResult = await launchCheckoutPaymentGateway({
          gateway: result.payment_gateway,
          student: result.student,
          description: result.class?.name,
        });

        setIsSubmitting(true);
        const verifyResponse = await verifyCheckoutPaymentService(
          {
            purchase_id: result.purchase_id,
            razorpay_order_id: razorpayResult.razorpay_order_id,
            razorpay_payment_id: razorpayResult.razorpay_payment_id,
            razorpay_signature: razorpayResult.razorpay_signature,
          },
          result.payment_gateway.verify_url
        );

        if (!isVerifyPaymentComplete(verifyResponse.data?.payment_status)) {
          throw new Error(
            verifyResponse.message ||
              "Payment verification failed. Please try again."
          );
        }

        completeEnrollment();
        return;
      }

      clearCheckoutSession();
      navigate(
        `/checkout/success?email=${encodeURIComponent(values.email)}`,
        { replace: true }
      );
    } catch (err) {
      if (err instanceof Error && !isAxiosError(err)) {
        const message = err.message || "Checkout unavailable";
        if (message !== "Payment cancelled.") {
          errorToast("Checkout unavailable", message);
        }
      }
    } finally {
      setIsSubmitting(false);
    }
  };

  const backLink =
    checkoutData.categorySlug && checkoutData.courseSlug
      ? buildClassCatalogPath(
          checkoutData.categorySlug,
          checkoutData.courseSlug
        )
      : checkoutData.levelId && checkoutData.classId
        ? `/classes/level/${checkoutData.levelId}/${checkoutData.classId}`
        : "/classes";

  return (
    <div className={`bg-landing-page py-8 md:py-10 ${LANDING_PAGE_GUTTER}`}>
      <Link
        to={backLink}
        className="mb-6 inline-flex items-center gap-1 text-[14px] font-medium text-landing-button hover:underline"
      >
        <LandingIcon name="chevronLeft" className="h-4 w-4" />
        Back
      </Link>

      <div className="grid gap-10 md:grid-cols-[minmax(0,1fr)_375px] md:items-start md:gap-10">
        <div>
          <h1 className={`mb-8 ${LANDING_CHECKOUT_TYPOGRAPHY.pageTitle}`}>
            Checkout
          </h1>
          <CheckoutForm onSubmit={handleSubmit} isSubmitting={isSubmitting} />
        </div>

        <div className={LANDING_CHECKOUT_TYPOGRAPHY.orderSummarySection}>
          <h2 className={LANDING_CHECKOUT_TYPOGRAPHY.orderSummaryTitle}>
            Order Summary
          </h2>
          <OrderSummaryCard
            course={checkoutData.course}
            className="mx-auto md:mx-0 md:sticky md:top-[var(--landing-sticky-top)]"
          />
        </div>
      </div>
    </div>
  );
}
